import { ApolloError } from '@apollo/client'
import { Body, Button, colors, Form, Spacer, useAlert } from '@spritz-finance/ui'
import React, { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Checkbox } from '../../../../components/Checkbox'
import { Row } from '../../../../components/Layout/Row'
import { useCurrentUser } from '../../../../context/useCurrentUser'
import {
    AccountType,
    useAddBankAccountMutation,
    useLookupInstitutionQuery,
} from '../../../../graphql/generated/graphql'
import { cssVar } from '../../../../theme/utils'
import { toTitleCase } from '../../../../utils/string'
import styled from 'styled-components'

type AddAccountFormData = {
    accountNumber: string
    confirmAccountNumber: string
    routingNumber: string
    type: AccountType
    holder?: string
    email?: string
    name: string
}

const getErrorMessage = (mutationError: ApolloError | undefined) => {
    const error = mutationError?.graphQLErrors?.[0].message ?? null
    let message = error
    if (message) {
        if (message.match(/Invalid routing number/i)) {
            message =
                "We don't recognise that routing number. Please check the number and try again."
        }
    }
    return message
}

interface AddAccountFormProps {
    onComplete: () => void
    payOthers?: boolean
}

const RoutingNumberContainer = styled.div`
    position: relative;
`

const RoutingNumberDetails = styled.div`
    position: absolute;
    top: 0px;
    right: 10px;
    height: 48px;
    display: flex;
    flex-direction: row;
    line-height: 48px;

    img {
        margin-top: 8px;
        width: 32px;
        height: 32px;
        margin-left: 16px;
    }
`

export const AddAccountForm: React.VFC<AddAccountFormProps> = ({ onComplete, payOthers }) => {
    const { me } = useCurrentUser()
    const alert = useAlert()
    const [addBankAccount, { loading, error }] = useAddBankAccountMutation()
    const [acceptedTerms, setAcceptedTerms] = useState(!!payOthers)

    const onSubmit: SubmitHandler<AddAccountFormData> = async ({
        accountNumber,
        routingNumber,
        type,
        holder,
        name,
    }) => {
        await addBankAccount({
            variables: {
                createAccountInput: {
                    email: me.email,
                    accountNumber,
                    routingNumber,
                    name,
                    type,
                    holder: holder!,
                    ownedByUser: !payOthers,
                },
            },
            refetchQueries: ['BankAccounts', 'BillsData'],
            awaitRefetchQueries: true,
            onCompleted: () => {
                onComplete?.()
                alert.success('Successfully added your account!')
            },
        })
    }

    const errorMessage = getErrorMessage(error)

    const initialValues = { holder: !payOthers ? `${me.firstName} ${me.lastName}` : '' }

    const methods = useForm<AddAccountFormData>({
        defaultValues: initialValues,
        reValidateMode: 'onBlur',
    })

    const routingNumber = methods.watch('routingNumber')

    const { data: institutionData } = useLookupInstitutionQuery({
        variables: {
            routingNumber,
        },
        skip: !routingNumber,
    })

    return (
        <Form<AddAccountFormData> width="auto" onSubmit={onSubmit} methods={methods}>
            <Form.Input
                autoComplete="off"
                name="name"
                placeholder="Account nickname"
                validation={{ required: true }}
            />

            <RoutingNumberContainer>
                <Form.Input
                    autoComplete="off"
                    name="routingNumber"
                    type="number"
                    placeholder="ACH routing number"
                    validationMessage="Routing number must be 9 numbers long"
                    validation={{
                        validate: {
                            validLength: (number) => {
                                if (!number || number.length !== 9) {
                                    return 'Routing number must be 9 digits'
                                }
                            },
                        },
                    }}
                />
                {institutionData?.achInstitution && (
                    <RoutingNumberDetails>
                        {institutionData.achInstitution.name}
                        {institutionData.achInstitution?.logo && (
                            <img src={institutionData.achInstitution.logo} />
                        )}
                    </RoutingNumberDetails>
                )}
            </RoutingNumberContainer>

            <Form.Input
                autoComplete="off"
                type="number"
                name="accountNumber"
                placeholder="Account number"
                validation={{ required: true }}
            />
            <Form.Select<AccountType>
                name="type"
                placeholder="Account type"
                validation={{ required: true }}
                options={[AccountType.Checking, AccountType.Savings, AccountType.Business]}
                renderLabel={(option) => toTitleCase(option)}
            />

            {payOthers && (
                <Form.Input
                    autoComplete="off"
                    name="holder"
                    validation={{ required: true }}
                    placeholder="Account holder name"
                />
            )}

            {payOthers && (
                <Form.Input
                    autoComplete="off"
                    name="email"
                    placeholder="Account holder email (optional)"
                />
            )}

            {!payOthers && (
                <Row>
                    <Checkbox
                        checked={acceptedTerms}
                        onChange={() => setAcceptedTerms((acc) => !acc)}
                    />
                    <Spacer width={8} />
                    <Body.SmBold size={12}>
                        I confirm that I am the holder of the above account, and that the details
                        provided are correct
                    </Body.SmBold>
                </Row>
            )}
            <Spacer height={32} />
            {errorMessage && (
                <>
                    <Row align="center">
                        <Body.Med size={12} color={cssVar('--text-error', colors.orchid)}>
                            {errorMessage}
                        </Body.Med>
                    </Row>
                    <Spacer height={16} />
                </>
            )}
            <Row align="center">
                <Button.CTA type="submit" disabled={!acceptedTerms} loading={loading}>
                    Confirm
                </Button.CTA>
            </Row>
            <Spacer height={32} />
        </Form>
    )
}
